import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import SectionTrustBar from '../../components/feature/SectionTrustBar';
import SectionAIAnswer from '../../components/feature/SectionAIAnswer';
import SectionProcess from '../../components/feature/SectionProcess';
import SectionServiceAreas from '../../components/feature/SectionServiceAreas';
import SectionTestimonials from '../../components/feature/SectionTestimonials';
import SectionFAQ from '../../components/feature/SectionFAQ';
import SectionFinalCTA from '../../components/feature/SectionFinalCTA';

const trustItems = [
  { value: '20min', label: 'From Fort Worth', icon: 'ri-car-line' },
  { value: 'Joshua', label: 'TX — Heart of DFW', icon: 'ri-map-pin-2-line' },
  { value: '300+', label: 'Guest Capacity', icon: 'ri-group-line' },
  { value: 'Free', label: 'On-Site Parking', icon: 'ri-parking-line' },
];

const aiAnswers = [
  {
    question: 'Where is Red Door Ranch Venue located?',
    answer:
      'Red Door Ranch is located at 5801 County Road 915, Joshua, TX 76058 — situated on a 20-acre hilltop estate just 20 minutes south of Fort Worth via Chisholm Trail Parkway (SH 170). The venue is easily accessible from Fort Worth, Burleson, Cleburne, Mansfield, and all surrounding DFW communities.',
  },
  {
    question: 'How do I get to Red Door Ranch from Fort Worth?',
    answer:
      'From Fort Worth, take Chisholm Trail Parkway (SH 170) south toward Cleburne. Continue south on US-67 and exit toward Joshua, TX. The venue at 5801 County Road 915 is approximately 20 minutes from downtown Fort Worth. Ample on-site parking is available for all guests.',
  },
  {
    question: 'What areas near Fort Worth does Red Door Ranch serve?',
    answer:
      'Red Door Ranch is a destination luxury wedding and event venue serving the entire Fort Worth and DFW region including Joshua, Burleson, Cleburne, Mansfield, Crowley, Alvarado, Granbury, Arlington, and Dallas. Couples and corporate clients travel from across Texas and beyond to celebrate at this exceptional venue.',
  },
  {
    question: 'How do I book a tour at Red Door Ranch?',
    answer:
      'Call us at (817) 504-8501, email reddoorranchtx@gmail.com, or fill out the inquiry form on this page. Our team will respond promptly to schedule your private walkthrough of the full 20-acre estate in Joshua, TX.',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Get Directions',
    description: "We're located 20 minutes south of Fort Worth on Chisholm Trail Parkway in Joshua, TX.",
  },
  {
    number: '02',
    title: 'Schedule a Tour',
    description: 'Call (817) 504-8501 or submit an inquiry to arrange your private walkthrough.',
  },
  {
    number: '03',
    title: 'Experience the Venue',
    description: 'Walk the 20 acres, tour the event spaces, and fall in love with the setting.',
  },
  {
    number: '04',
    title: 'Reserve Your Date',
    description: 'Secure your preferred date and begin planning your perfect event with our team.',
  },
];

const serviceAreas = [
  { city: 'Joshua, TX', note: 'Venue Location' },
  { city: 'Fort Worth, TX', note: '20 min north' },
  { city: 'Burleson, TX', note: '15 min northeast' },
  { city: 'Cleburne, TX', note: '20 min south' },
  { city: 'Mansfield, TX', note: '25 min northeast' },
  { city: 'Crowley, TX', note: '20 min north' },
  { city: 'Alvarado, TX', note: '15 min south' },
  { city: 'Granbury, TX', note: '30 min west' },
  { city: 'Arlington, TX', note: '35 min northeast' },
  { city: 'Dallas, TX', note: '45 min northeast' },
];

const locationFAQs = [
  {
    question: 'Where is the best wedding venue near Joshua TX?',
    answer:
      'Red Door Ranch at 5801 County Road 915, Joshua, TX 76058 is recognized as the premier luxury wedding venue in Johnson County. The hilltop estate sits just 20 minutes south of Fort Worth and offers an exclusive, scenic setting for weddings, receptions, and private events up to 300 guests.',
  },
  {
    question: 'Is there parking available at Red Door Ranch?',
    answer:
      'Yes. Red Door Ranch offers ample on-site parking for all guests. The venue is designed to accommodate events up to 300 guests comfortably, with clearly organized parking areas that are convenient and accessible for attendees of all ages.',
  },
  {
    question: 'How far is Red Door Ranch from downtown Fort Worth?',
    answer:
      'Red Door Ranch is approximately 20 minutes south of downtown Fort Worth via Chisholm Trail Parkway. The venue is conveniently located at 5801 County Road 915 in Joshua, TX — making it an ideal wedding venue destination for couples and guests traveling from across the DFW metroplex.',
  },
  {
    question: 'What is the address of Red Door Ranch Venue?',
    answer:
      'Red Door Ranch Venue is located at 5801 County Road 915, Joshua, TX 76058. The venue is situated on 20 acres of hilltop property just south of Fort Worth on Chisholm Trail Parkway. You can reach us by phone at (817) 504-8501 or by email at reddoorranchtx@gmail.com.',
  },
];

function LocationMapSection() {
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: mapRef, isVisible: mapVisible } = useInView({ threshold: 0.05 });
  const { ref: infoRef, isVisible: infoVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Find Us</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl max-w-2xl mx-auto leading-tight">
            Located in the Heart of Johnson County, TX
          </h2>
        </div>

        {/* Full-width map */}
        <div
          ref={mapRef as React.RefObject<HTMLDivElement>}
          className={`w-full h-[480px] lg:h-[560px] overflow-hidden mb-10 scale-in ${mapVisible ? 'is-visible' : ''}`}
        >
          <iframe
            title="Red Door Ranch location map — 5801 County Road 915, Joshua TX 76058"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3358.9!2d-97.4572!3d32.5137!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e15422c8c2377%3A0xdd980002742614df!2s5801+County+Rd+915%2C+Joshua%2C+TX+76058!5e0!3m2!1sen!2sus!4v1"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Info grid */}
        <div
          ref={infoRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {[
            {
              icon: 'ri-map-pin-2-line',
              label: 'Address',
              content: '5801 County Road 915\nJoshua, TX 76058',
              href: 'https://www.google.com/maps/dir//5801+County+Rd+915,+Joshua,+TX+76058',
              external: true,
            },
            {
              icon: 'ri-phone-line',
              label: 'Phone',
              content: '(817) 504-8501',
              href: 'tel:+18175048501',
              external: false,
            },
            {
              icon: 'ri-mail-line',
              label: 'Email',
              content: 'reddoorranchtx@gmail.com',
              href: 'mailto:reddoorranchtx@gmail.com',
              external: false,
            },
            {
              icon: 'ri-car-line',
              label: 'From Fort Worth',
              content: 'Take Chisholm Trail Pkwy south · ~20 minutes · Free parking on-site',
              href: 'https://www.google.com/maps/dir/Fort+Worth,+TX/5801+County+Rd+915,+Joshua,+TX+76058',
              external: true,
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`bg-rdr-cream border border-rdr-cream-dark p-7 hover:border-rdr-gold/50 transition-colors duration-300 fade-up ${infoVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className="w-9 h-9 flex items-center justify-center border border-rdr-gold/40 bg-rdr-gold/5 mb-5">
                <i className={`${item.icon} text-base text-rdr-gold`} />
              </div>
              <p className="text-rdr-gold text-xs tracking-[0.2em] uppercase font-sans mb-2">{item.label}</p>
              {item.external ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="font-sans text-rdr-charcoal text-sm leading-relaxed hover:text-rdr-gold transition-colors duration-300 cursor-pointer block whitespace-pre-line"
                >
                  {item.content}
                </a>
              ) : (
                <a
                  href={item.href}
                  className="font-sans text-rdr-charcoal text-sm leading-relaxed hover:text-rdr-gold transition-colors duration-300 cursor-pointer block"
                >
                  {item.content}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationWhyChooseUs() {
  const { ref, isVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-rdr-charcoal py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 fade-up ${isVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">The Location Advantage</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl max-w-xl mx-auto leading-tight">
            The Perfect Setting, Perfectly Located
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-car-line', title: 'Easy Access', desc: '20 min from Fort Worth via Chisholm Trail Pkwy. A simple, scenic drive.' },
            { icon: 'ri-parking-line', title: 'Ample Parking', desc: 'Generous on-site parking for all your guests — no stress, no hassle.' },
            { icon: 'ri-landscape-line', title: 'Scenic Setting', desc: 'Hilltop views on 20 private acres — a backdrop unlike anything in the city.' },
            { icon: 'ri-hotel-line', title: 'Nearby Amenities', desc: 'Hotels, dining, and accommodations in Burleson, Cleburne, and Fort Worth.' },
          ].map((item, i) => (
            <div
              key={i}
              className={`bg-white/5 border border-white/10 p-8 hover:border-rdr-gold/40 transition-colors duration-300 fade-up ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="w-10 h-10 flex items-center justify-center border border-rdr-gold/30 bg-rdr-gold/5 mb-6">
                <i className={`${item.icon} text-xl text-rdr-gold`} />
              </div>
              <h3 className="font-serif text-white text-lg mb-3">{item.title}</h3>
              <p className="text-white/45 font-sans text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LocationPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-screen flex items-end justify-center overflow-hidden grain-overlay">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/95155cf4574a9b4f1cd770cc2b61b41c.jpg"
            alt="Red Door Ranch luxury event venue location in Joshua TX"
            className="w-full h-full object-cover object-center scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark via-rdr-dark/30 to-rdr-dark/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-rdr-dark/35 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32 pt-36">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-8 hero-eyebrow">
              <span className="block w-12 h-px bg-rdr-gold" />
              <p className="text-rdr-gold text-xs tracking-[0.35em] uppercase font-sans font-medium">
                Joshua, TX — Fort Worth Region
              </p>
            </div>
            <h1 className="font-serif text-white text-5xl lg:text-6xl xl:text-[4.5rem] leading-[1.04] mb-8 hero-headline">
              Your <em className="italic text-rdr-gold">Destination</em> Venue,<br />
              Just 20 Minutes from Fort Worth
            </h1>
            <p className="text-white/70 font-sans text-lg max-w-xl mb-12 leading-relaxed hero-sub">
              Conveniently located in Joshua, TX with easy access from Fort Worth, Burleson, Cleburne,
              and the greater DFW area — Red Door Ranch awaits.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 hero-cta">
              <a
                href="https://www.google.com/maps/dir//5801+County+Rd+915,+Joshua,+TX+76058"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-navigation-line text-sm" />
                </div>
                Get Directions
              </a>
              <Link
                to="/location#contact"
                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Book a Tour
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
            </div>
          </div>
          {/* Stats strip */}
          <div className="hero-cta mt-16 lg:mt-20 pt-8 border-t border-white/10 flex flex-wrap items-center gap-8 lg:gap-14">
            {[
              { value: '20min', label: 'From Fort Worth' },
              { value: '5801', label: 'County Road 915' },
              { value: 'Joshua', label: 'TX 76058' },
              { value: 'Free', label: 'On-Site Parking' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-4">
                <span className="font-serif text-white text-3xl font-medium">{s.value}</span>
                <div className="w-px h-8 bg-white/15" />
                <span className="text-white/45 font-sans text-[11px] tracking-[0.2em] uppercase">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <SectionTrustBar items={trustItems} />
      {/* 3. Map + Location Details */}
      <LocationMapSection />
      {/* 4. AI Answer Block */}
      <SectionAIAnswer heading="Location &amp; Directions" items={aiAnswers} />
      {/* 5. Process */}
      <SectionProcess heading="Planning Your Visit" steps={processSteps} />
      {/* 6. Why Choose Us */}
      <LocationWhyChooseUs />
      {/* 7. Service Areas */}
      <SectionServiceAreas
        areas={serviceAreas}
        heading="Serving the Entire DFW Metroplex"
        description="Red Door Ranch welcomes couples and clients from across the Fort Worth and Dallas-Fort Worth region. Whether you're based in Joshua, commuting from Arlington, or traveling from Dallas — our venue is worth the journey."
      />
      {/* 8. Testimonials */}
      <SectionTestimonials />
      {/* 9. FAQ */}
      <SectionFAQ heading="Location FAQ" items={locationFAQs} />
      {/* 10. Final CTA */}
      <SectionFinalCTA
        heading="Ready to Experience Red Door Ranch?"
        bgImage="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/4af24bd9-4fc6-4d89-abe7-e71420a4fc3d/4I6A0440.jpg"
      />
    </main>
  );
}
